import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = {
  primary: [99, 102, 241], // indigo-500
  secondary: [148, 163, 184], // slate-400
  success: [52, 211, 153], // green-400
  warning: [251, 191, 36], // amber-400
  danger: [248, 113, 113], // red-400
  text: [30, 41, 59], // slate-800
  lightText: [100, 116, 139], // slate-500
  background: [248, 250, 252], // slate-50
};

const addHeader = (doc, title, orgName, dateRange) => {
  // Header background
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, doc.internal.pageSize.width, 40, 'F');
  
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 20);
  
  // Subtitle
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  let subtitle = orgName || 'RiskOS Report';
  if (dateRange) {
    subtitle += ` | ${dateRange}`;
  }
  doc.text(subtitle, 14, 32);
  
  // Generated timestamp
  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleString()}`, doc.internal.pageSize.width - 14, 32, { align: 'right' });
  
  return 50; // Return Y position after header
};

const addFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.lightText);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
    doc.text(
      'Powered by RiskOS | Players Health',
      14,
      doc.internal.pageSize.height - 10
    );
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Export Incidents Report
export const exportIncidentsReport = (incidents, orgName, dateRange) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Incidents Report', orgName, dateRange);
  
  // Summary Stats
  const totalIncidents = incidents.length;
  const byStatus = incidents.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});
  const bySeverity = incidents.reduce((acc, i) => {
    acc[i.severity] = (acc[i.severity] || 0) + 1;
    return acc;
  }, {});
  
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 14, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Incidents: ${totalIncidents}`, 14, yPos);
  yPos += 6;
  doc.text(`Open: ${byStatus.new || 0} | In Review: ${byStatus.in_review || 0} | Closed: ${byStatus.closed || 0}`, 14, yPos);
  yPos += 6;
  doc.text(`Critical: ${bySeverity.critical || 0} | High: ${bySeverity.high || 0} | Medium: ${bySeverity.medium || 0} | Low: ${bySeverity.low || 0}`, 14, yPos);
  yPos += 15;
  
  // Incidents Table
  const tableData = incidents.map(incident => [
    incident.title?.substring(0, 30) + (incident.title?.length > 30 ? '...' : ''),
    incident.primary_category || incident.category || '-',
    (incident.severity || 'medium').toUpperCase(),
    (incident.status || 'new').replace('_', ' ').toUpperCase(),
    new Date(incident.incident_date || incident.created_at).toLocaleDateString(),
    incident.assigned_admin_name || '-'
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Title', 'Category', 'Severity', 'Status', 'Date', 'Assigned To']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: COLORS.text
    },
    alternateRowStyles: {
      fillColor: COLORS.background
    },
    columnStyles: {
      0: { cellWidth: 50 },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' }
    }
  });
  
  addFooter(doc);
  doc.save(`incidents-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

// Export Assessments Report
export const exportAssessmentsReport = (assessments, orgName) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Safety Assessments Report', orgName);
  
  // Summary
  const avgScore = assessments.length > 0 
    ? Math.round(assessments.reduce((sum, a) => sum + (a.score || 0), 0) / assessments.length) 
    : 0;
  const completedCount = assessments.filter(a => a.status === 'completed').length;
  
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 14, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Assessments: ${assessments.length}`, 14, yPos);
  yPos += 6;
  doc.text(`Completed: ${completedCount}`, 14, yPos);
  yPos += 6;
  doc.text(`Average Score: ${avgScore}%`, 14, yPos);
  yPos += 15;
  
  // Assessments Table
  const tableData = assessments.map(assessment => [
    assessment.assessment_type || 'General',
    `${assessment.score || 0}%`,
    (assessment.risk_level || 'N/A').toUpperCase(),
    assessment.status || 'draft',
    new Date(assessment.created_at).toLocaleDateString(),
    assessment.organization_name || '-'
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Type', 'Score', 'Risk Level', 'Status', 'Date', 'Organization']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: COLORS.text
    },
    alternateRowStyles: {
      fillColor: COLORS.background
    }
  });
  
  addFooter(doc);
  doc.save(`assessments-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

// Export Claims Report
export const exportClaimsReport = (claims, orgName, analytics) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Claims Report', orgName);
  
  // Summary
  const totalAmount = claims.reduce((sum, c) => sum + (c.amount || 0), 0);
  const avgAmount = claims.length > 0 ? totalAmount / claims.length : 0;
  
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary', 14, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total Claims: ${claims.length}`, 14, yPos);
  yPos += 6;
  doc.text(`Total Exposure: ${formatCurrency(totalAmount)}`, 14, yPos);
  yPos += 6;
  doc.text(`Average Claim Amount: ${formatCurrency(avgAmount)}`, 14, yPos);
  yPos += 15;
  
  // Claims Table
  const tableData = claims.map(claim => [
    claim.claim_number || '-',
    claim.claim_type || '-',
    formatCurrency(claim.amount),
    (claim.status || 'open').toUpperCase(),
    new Date(claim.date_of_loss || claim.created_at).toLocaleDateString(),
    claim.description?.substring(0, 40) + (claim.description?.length > 40 ? '...' : '') || '-'
  ]);
  
  autoTable(doc, {
    startY: yPos,
    head: [['Claim #', 'Type', 'Amount', 'Status', 'Date', 'Description']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: COLORS.text
    },
    alternateRowStyles: {
      fillColor: COLORS.background
    }
  });
  
  addFooter(doc);
  doc.save(`claims-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

// Export Risk Overview Report
export const exportRiskOverviewReport = (intelligence, orgName) => {
  const doc = new jsPDF();
  let yPos = addHeader(doc, 'Risk Overview Report', orgName);
  
  const org = intelligence?.organization;
  const assessments = intelligence?.assessments || [];
  const incidents = intelligence?.incidents_summary || {};
  const claims = intelligence?.claims_summary || {};
  const alerts = intelligence?.alerts || [];
  
  // Organization Info
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.text);
  doc.setFont('helvetica', 'bold');
  doc.text('Organization Overview', 14, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${org?.name || orgName || 'N/A'}`, 14, yPos);
  yPos += 6;
  doc.text(`Type: ${org?.org_type || 'N/A'} | Level: ${org?.level || 'N/A'}`, 14, yPos);
  yPos += 6;
  if (org?.ein) {
    doc.text(`EIN: ${org.ein}`, 14, yPos);
    yPos += 6;
  }
  yPos += 10;
  
  // Safety Score
  if (assessments[0]) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Safety Assessment', 14, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Current Score: ${assessments[0].score || 0}%`, 14, yPos);
    yPos += 6;
    doc.text(`Risk Level: ${(assessments[0].risk_level || 'N/A').toUpperCase()}`, 14, yPos);
    yPos += 10;
  }
  
  // Key Metrics Table
  yPos += 5;
  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value']],
    body: [
      ['Total Incidents', incidents.total_incidents || 0],
      ['Open Incidents', incidents.by_status?.open || 0],
      ['Total Claims', claims.total_claims || 0],
      ['Claims Exposure', formatCurrency(claims.total_amount)],
      ['Active Alerts', alerts.filter(a => !a.is_read).length]
    ],
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 10,
      textColor: COLORS.text
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 100 },
      1: { halign: 'right' }
    }
  });
  
  addFooter(doc);
  doc.save(`risk-overview-${orgName?.replace(/\s+/g, '-').toLowerCase() || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`);
};

export default {
  exportIncidentsReport,
  exportAssessmentsReport,
  exportClaimsReport,
  exportRiskOverviewReport
};
